// EncryptionBadge — room-header lock and per-message shield glyphs.
//
// The brief reserves shields for signal, not decoration. The
// per-message shield is silent on `unencrypted` (not a statement worth
// making) and silent on `verified` at rest; we render it for
// `verified` only when the caller explicitly asks (the timeline may
// prefer silent-when-verified, the details panel may prefer always-on).
// `unverified` and `undecryptable` always render — the user needs the
// signal to decide whether to trust the message.
//
// Every value is a `--chat-*` custom property. No pixel sizes are
// hard-coded: `size-*` Tailwind classes map to the spacing scale; the
// brief's 16×16 room-header lock and 12×12 shield each have a dedicated
// class.

import { Lock, Shield, ShieldAlert, ShieldCheck } from "lucide-react";

import type { MessageEncryptionStatus } from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

export type EncryptionBadgeVariant = "room-header" | "message";

export interface EncryptionBadgeProps {
  /** "room-header" renders the 16×16 lock glyph shown next to the room
   *  name on encrypted rooms. "message" renders the 12×12 shield on a
   *  single message, coloured by status. */
  variant: EncryptionBadgeVariant;
  /** Status drives colour + tooltip copy. Ignored for "room-header":
   *  the lock is a presence indicator, not a state indicator. */
  status?: MessageEncryptionStatus;
  /** For "room-header": render the lock in the cross-signed state
   *  (`encryption-verified`) or the default unverified colour
   *  (`encryption-unverified`). */
  roomVerified?: boolean;
  /** Optional override for accessible name. Default copy is derived
   *  from the status — callers should only override in reviews/tests. */
  label?: string;
  /** When true on a per-message shield, render even for `verified`.
   *  Timeline callers leave this false so verified sessions stay
   *  visually silent; the message-detail panel sets it. */
  renderWhenVerified?: boolean;
}

/**
 * Human-readable tooltip copy for a given status. Exported so the
 * banner and UTD placeholder can share the same wording.
 */
export function encryptionTooltipCopy(status: MessageEncryptionStatus): string {
  switch (status) {
    case "verified":
      return "Encrypted from a verified device";
    case "unverified":
      return "Encrypted, but the sending device isn't verified yet";
    case "undecryptable":
      return "Unable to decrypt. The session key is missing from this device.";
    case "unencrypted":
      return "Not encrypted";
  }
}

export function EncryptionBadge(props: EncryptionBadgeProps) {
  if (props.variant === "room-header") {
    const label = props.label ?? "Encrypted room";
    return (
      <Lock
        aria-label={label}
        role="img"
        strokeWidth={ICON_STROKE}
        className="size-4 shrink-0"
        style={{
          color: props.roomVerified
            ? "var(--chat-encryption-verified)"
            : "var(--chat-encryption-unverified)",
          marginInlineEnd: "var(--chat-space-1)",
        }}
      />
    );
  }

  const status = props.status ?? "unverified";
  if (status === "unencrypted") return null;
  if (status === "verified" && !props.renderWhenVerified) return null;

  const { Icon, color } = iconFor(status);
  const label = props.label ?? encryptionTooltipCopy(status);

  return (
    <span
      // `title` gives the hover tooltip. Custom tooltip surfaces live
      // in a shared primitive; the plain `title` is the honest
      // fallback until that lands.
      title={label}
      className="inline-flex shrink-0 items-center justify-center"
      style={{ width: 12, height: 12 }}
    >
      <Icon
        role="img"
        aria-label={label}
        strokeWidth={ICON_STROKE}
        // 12×12 matches the brief's per-message shield spec. No
        // Tailwind size-3 alias exists, so we size directly via the
        // wrapper above and let the icon fill.
        style={{ width: 12, height: 12, color }}
      />
    </span>
  );
}

function iconFor(status: MessageEncryptionStatus): {
  Icon: typeof ShieldCheck;
  color: string;
} {
  switch (status) {
    case "verified":
      return { Icon: ShieldCheck, color: "var(--chat-encryption-verified)" };
    case "unverified":
      return { Icon: Shield, color: "var(--chat-encryption-unverified)" };
    case "undecryptable":
      return { Icon: ShieldAlert, color: "var(--chat-encryption-utd)" };
    case "unencrypted":
      // Not reachable — guarded above. Keeping the branch keeps the
      // switch exhaustive for `noFallthroughCasesInSwitch`.
      return { Icon: Shield, color: "var(--chat-fg-muted)" };
  }
}
